import { SubtleButton } from 'component/button';
import { colorTextPrimary, colorTextSecondary } from 'component/color';
import type { CommentConfig } from 'component/comment';
import { commentTable } from 'component/comment';
import { IconDownvote, IconDownvoted } from 'component/icon';
import type { UpvoteConfig } from 'feature/comments/upvote';
import { useState } from 'react';
import { useCollection, useDatastore, useObject, usePersonaKey } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { useIsReadOnly } from 'util/features';
import { downvoteTable, upvoteTable } from 'util/stdconfig';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export const DownvoteFeature: Feature<CommentConfig & UpvoteConfig> = {
    name: 'Downvote Comments',
    key: 'downvote',
    dev: true,
    providesConfig: {
        commentActions: [ActionDownvote],
        upvoteOnUpvoteTriggers: [onUpvoteTriggerAsync],
    },
    missingConfigAllowed: ['upvoteOnUpvoteTriggers'],
};

export async function onUpvoteTriggerAsync({ datastore, commentKey }) {
    const personaKey = datastore.getPersonaKey();
    const downvotes = datastore.getCollection(downvoteTable, { filter: { comment: commentKey } });
    const downvoted = downvotes.some((upvote) => upvote.from == personaKey);
    if (downvoted) {
        logEventAsync(datastore, 'downvote-undo', { commentKey });
        const myDownvote = downvotes.find((downvote) => downvote.from == personaKey);
        if (myDownvote) {
            await datastore.deleteObject(downvoteTable, myDownvote.key);
        }
    }
}

export function ActionDownvote({ commentKey }) {
    const personaKey = usePersonaKey();
    const comment = useObject(commentTable, commentKey);
    const downvotes = useCollection(downvoteTable, { filter: { comment: commentKey } });
    const upvotes = useCollection(upvoteTable, { filter: { comment: commentKey } });
    const upvoted = upvotes.some((upvote) => upvote.from == personaKey);
    const datastore = useDatastore();
    const readOnly = useIsReadOnly();
    const count = downvotes.length;
    const downvoted = downvotes.some((upvote) => upvote.from == personaKey);
    const alwaysDisabled = comment?.from == personaKey || readOnly;
    const [isDisabled, setIsDisabled] = useState(false);

    async function onDownvote() {
        setIsDisabled(true);
        await handleDownvoteAsync({ datastore, personaKey, commentKey, downvoted, downvotes, upvoted, upvotes });
        setIsDisabled(false);
    }

    return (
        <SubtleButton
            ariaLabel={t('Downvote')}
            color={downvoted ? colorTextPrimary : colorTextSecondary}
            disabled={alwaysDisabled || isDisabled}
            icon={downvoted ? IconDownvoted : IconDownvote}
            onPress={datastore.needsLogin(onDownvote, 'downvote')}
            padRight
            testID='downvote'
            text={count.toString()}
        />
    );
}

export async function handleDownvoteAsync({ datastore, personaKey, commentKey, downvoted, downvotes, upvoted, upvotes }) {
    if (!personaKey) return;

    if (upvoted) {
        logEventAsync(datastore, 'upvote-undo', { commentKey });
        const myUpvote = upvotes.find((upvote) => upvote.from == personaKey);
        if (myUpvote) {
            await datastore.deleteObject(upvoteTable, myUpvote.key);
        }
    }

    if (downvoted) {
        logEventAsync(datastore, 'downvote-undo', { commentKey });
        const myDownvote = downvotes.find((downvote) => downvote.from == personaKey);
        if (myDownvote) {
            await datastore.deleteObject(downvoteTable, myDownvote.key);
        }
    } else {
        await datastore.addObject(downvoteTable, { comment: commentKey, from: personaKey });
        logEventAsync(datastore, downvoteTable, { commentKey });
    }
}
